/**
 * wizard-state.js
 */

import { Types } from 'utils/constants';
import { makeLocation, makeDeploymentSectionPath } from 'utils/routing';


export default function getWizardState(deploymentRecord, step, basePath, history) {
  if (!deploymentRecord || deploymentRecord.isComplete) {
    return { hasNext: false, nextLocation: null };
  }

  const workMode = deploymentRecord.workMode;
  const address1 = deploymentRecord.location.address1;
  const currentStep = step ? step[0] : null;
  let nextLocation = null;
  
  // Check if deployment is "Not performed" based on fulfillmentState
  const isNotPerformed = deploymentRecord.fulfillmentState === Types.FulfillmentState.DogNotDeployed || 
                         deploymentRecord.fulfillmentState === Types.FulfillmentState.CancelledEnroute;

  // STEP 1 completion-check.
  const step1Complete = Boolean(address1);

  // STEP 2 completion-check.
  // We have to specifically check if a tracking deployment is incomplete since tracking
  // data is auto-saved after tracking completes but the tracking record is still technically incomplete.
  const hasPartialTracking = (() => {
    const tracking = deploymentRecord.patrol?.patrolTypes[0]?.tracking;
    return tracking && tracking.terrainTypes.length === 0;
  })();
  
  // For "Not performed" deployments, always consider step 2 complete
  const step2Complete = isNotPerformed || Boolean(
    workMode === Types.WorkMode.DETECTION
    || (deploymentRecord.patrol.patrolTypes.length > 0 && !hasPartialTracking)
  );

  // STEP 3 completion-check.
  const step3Complete = Boolean(deploymentRecord && deploymentRecord.summary.comments !== '');


  // determine if we should have a next step
  const hasNext = (
    (currentStep === '1' 
      && (
        (!step2Complete || (workMode === Types.WorkMode.DETECTION && !isNotPerformed))
        || !step3Complete
      )
    )
    || (currentStep === '2' && !step3Complete)
    || (currentStep === '3' && !step2Complete && !isNotPerformed)
    || (currentStep === null && (!step1Complete || !step2Complete || !step3Complete))
  );

  
  // if we should have a next step, determine what it should be
  if (hasNext) {

    // GO TO STEP 1
    if (currentStep === null && !address1) {
      const step1SectionPath = makeDeploymentSectionPath(Types.DeploymentSection.GENERAL);
      nextLocation = makeLocation(
        basePath,
        'deployment',
        'edit',
        {
          deploymentId: deploymentRecord.id,
          viewSectionId: step1SectionPath,
          editSectionId: step1SectionPath,
        },
        history.location
      );
    }

    // GO TO EXISTING TRACKING STEP 2
    // Skip step 2 for "Not performed" deployments
    else if (!step2Complete && hasPartialTracking && !isNotPerformed) {
      const step2SectionPath = makeDeploymentSectionPath(Types.DeploymentSection.PATROL, deploymentRecord.patrol.patrolTypes[0]);
      nextLocation = makeLocation(
        basePath,
        'deployment',
        'edit',
        {
          deploymentId: deploymentRecord.id,
          viewSectionId: step2SectionPath,
          editSectionId: step2SectionPath,
        },
        history.location
      );
    }
      
    // GO TO NEW STEP 2
    // Skip step 2 for "Not performed" deployments
    else if (((currentStep === '1' && workMode === Types.WorkMode.DETECTION) || !step2Complete) && !isNotPerformed) {       
      const step2SectionPath = workMode === Types.WorkMode.DETECTION
        ? makeDeploymentSectionPath(Types.DeploymentSection.DETECTION, null)
        : makeDeploymentSectionPath(Types.DeploymentSection.PATROL, null);
      nextLocation = makeLocation(
        basePath,
        'deployment',
        'edit',
        {
          deploymentId: deploymentRecord.id,
          viewSectionId: step2SectionPath,
          editSectionId: step2SectionPath,
        },
        history.location
      );
    } 
    
    // GO TO STEP 3
    else if (!step3Complete) {
      const step3SectionPath = makeDeploymentSectionPath(Types.DeploymentSection.SUMMARY);
      nextLocation = makeLocation(
        basePath,
        'deployment',
        'edit',
        {
          deploymentId: deploymentRecord.id,
          viewSectionId: workMode === Types.WorkMode.DETECTION && !isNotPerformed ? step3SectionPath : Types.DeploymentSection.BUTTONSCROLL,
          editSectionId: step3SectionPath,
        },
        history.location
      );
    }
  }

  return {
    hasNext,
    nextLocation,
    isNotPerformed // Expose this property for other components to use
  };
}